import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTotalCountAction,
  fetchTotalRevenueAction,
  fetchTopUsersBySpendingAction,
  fetchTopUsersByPackagesAction,
  fetchTopDriversAction,
  fetchStatusDistributionAction,
  fetchDataByDateAction,
  fetchRecentTransactionsAction
} from "state/actions/analyticsActions";
import {
  Flex,
  Grid,
  SimpleGrid,
  useColorModeValue,
  Icon,
  Box
} from "@chakra-ui/react";
import {
  CartIcon,
  DocumentIcon,
  GlobeIcon,
  WalletIcon,
  DeliveryIcon,
  SupportIcon,
  LockerIcon,
  WarningIcon
} from "components/Icons/Icons.js";
import {
  FaArrowDown,
  FaArrowUp,
} from "react-icons/fa";
import ActiveUsers from "../Dashboard/components/ActiveUsers";
import MiniStatistics from "../Dashboard/components/MiniStatistics";
import SalesOverview from "../Dashboard/components/SalesOverview";
import BarChart from "components/Charts/BarChart";
import LineChart from "components/Charts/LineChart";
import { formatDate } from "utils/formatDate";

export default function Statistics() {
  const { t } = useTranslation();
  const iconBoxInside = useColorModeValue("white", "white");
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);

  // Redux state mappings
  const usersCount = useSelector(state => state.analytics.counts.users);
  const vehiclesCount = useSelector(state => state.analytics.counts.vehicles);
  const transactionsCount = useSelector(state => state.analytics.counts.transactions);
  const packagesCount = useSelector(state => state.analytics.counts.packages);
  const totalRevenue = useSelector(state => state.analytics.totalRevenue);
  const todaysRevenue = useSelector(state => state.analytics.todaysRevenue);
  const topUsersBySpending = useSelector(state => state.analytics.topUsersBySpending);
  const topUsersByPackages = useSelector(state => state.analytics.topUsersByPackages);
  const topDrivers = useSelector(state => state.analytics.topDrivers);
  const deliveryStatus = useSelector(state => state.analytics.statusDistribution.deliveries);
  const inProgressCount = deliveryStatus?.["in_progress"] || 0;
  const failedCount = deliveryStatus?.["failed"] || 0;
  const completedCount = deliveryStatus?.["completed"] || 0;
  const dataByDate = useSelector(state => state.analytics.dataByDate);
  const getLatestGrowth = (tableName) =>
    dataByDate?.[tableName]?.length
      ? dataByDate[tableName][dataByDate[tableName].length - 1]?.growth
      : 0;

  const usersGrowth = getLatestGrowth("users");
  const vehiclesGrowth = getLatestGrowth("vehicles");
  const transactionsGrowth = getLatestGrowth("transactions");
  const packagesGrowth = getLatestGrowth("packages");
  const recentTransactions = useSelector(state => state.analytics.recentTransactions);
  const getDirectionIcon = (current, previous) => {
    if (previous == null) return null;

    return current > previous
      ? <Icon as={FaArrowUp} color="green.400" boxSize={4} />
      : <Icon as={FaArrowDown} color="red.400" boxSize={4} />;
  };

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    // Dispatch actions to fetch data with correct table names
    dispatch(fetchTotalCountAction("users"));
    dispatch(fetchTotalCountAction("vehicles"));
    dispatch(fetchTotalCountAction("transactions"));
    dispatch(fetchTotalCountAction("packages"));
    dispatch(fetchTotalRevenueAction("transactions"));
    dispatch(fetchTopUsersBySpendingAction("transactions"));
    dispatch(fetchTopUsersByPackagesAction("packages"));
    dispatch(fetchStatusDistributionAction("deliveries"));
    dispatch(fetchDataByDateAction("users"));
    dispatch(fetchDataByDateAction("vehicles"));
    dispatch(fetchDataByDateAction("transactions"));
    dispatch(fetchDataByDateAction("packages"));
    dispatch(fetchTopDriversAction(100));
    dispatch(fetchRecentTransactionsAction(100));
  }, [dispatch]);


  // NEWEST
  let previousAmount = null;
  const newest = recentTransactions?.slice(0, 3).map((transaction, index) => {
    const icon = index === 0 ? null : getDirectionIcon(transaction.amount, previousAmount);
    previousAmount = transaction.amount;

    return {
      name: transaction.username || 'Unknown',
      logo: icon,
      date: formatDate(transaction.created_at),
      price: `P${transaction.amount.toFixed(2)}`
    };
  });

  // OLDER
  let previousOldAmount = null;
  const older = recentTransactions?.slice(-3).map((transaction, index) => {
    const icon = index === 0 ? null : getDirectionIcon(transaction.amount, previousOldAmount);
    previousOldAmount = transaction.amount;

    return {
      name: transaction.username || 'Unknown',
      logo: icon,
      date: formatDate(transaction.created_at),
      price: `P${transaction.amount.toFixed(2)}`,
    };
  });

  return (
    <Flex flexDirection='column'>
      <SimpleGrid columns={{ sm: 1, md: 2, xl: 4 }} spacing='24px' paddingBottom={"20px"}>
        <MiniStatistics
          title={"Today's Revenue"}
          amount={`P${todaysRevenue ? todaysRevenue : "0"}`}
          percentage={0}
          icon={<WalletIcon h={"24px"} w={"24px"} color={iconBoxInside} />}
          isLoading={isLoading}
        />
        <MiniStatistics
          title={'Total Users'}
          amount={usersCount && usersCount !== null ? usersCount : "0"}
          percentage={usersGrowth}
          icon={<GlobeIcon h={"24px"} w={"24px"} color={iconBoxInside} />}
          isLoading={isLoading}
        />
        <MiniStatistics
          title={'Total Packages'}
          amount={packagesCount?.toLocaleString() ?? "0"}
          percentage={packagesGrowth}
          icon={<DocumentIcon h={"24px"} w={"24px"} color={iconBoxInside} />}
          isLoading={isLoading}
        />

        <MiniStatistics
          title={t('total_sales')}
          amount={`P${totalRevenue ? totalRevenue : "0"}`}
          percentage={transactionsGrowth}
          icon={<CartIcon h={"24px"} w={"24px"} color={iconBoxInside} />}
          isLoading={isLoading}
        />
        <MiniStatistics
          title={t('active_deliveries')}
          amount={inProgressCount}
          percentage={0}
          icon={<DeliveryIcon h={"24px"} w={"24px"} color={iconBoxInside} />}
          isLoading={isLoading}
        />

        <MiniStatistics
          title={'Completed Deliveries'}
          amount={completedCount}
          percentage={0}
          icon={<LockerIcon h={"24px"} w={"24px"} color={iconBoxInside} />}
          isLoading={isLoading}
        />
        <MiniStatistics
          title={t('failed_deliveries')}
          amount={failedCount}
          percentage={0}
          icon={<WarningIcon h={"24px"} w={"24px"} color={iconBoxInside} />}
          isLoading={isLoading}
        />
        <MiniStatistics
          title={'Approved Vehicles'}
          amount={vehiclesCount?.toLocaleString() ?? "0"}
          percentage={vehiclesGrowth}
          icon={<SupportIcon h={"24px"} w={"24px"} color={iconBoxInside} />}
          isLoading={isLoading}
        />
      </SimpleGrid>

      <Grid templateColumns={{ sm: "1fr", lg: "1.3fr 1.7fr" }} templateRows={{ sm: "repeat(2, 1fr)", lg: "1fr" }} gap='24px' mb={{ lg: "26px" }}>
        <ActiveUsers
          title={t("active_users")}
          percentage={usersGrowth}
          chart={<BarChart title="Users" data={dataByDate?.users || []} />}
          isLoading={isLoading}
          
        />
        <SalesOverview
          title={t("sales_overview")}
          percentage={transactionsGrowth}
          chart={<LineChart data={dataByDate?.transactions || []} />}
          isLoading={isLoading}
        />
      </Grid>

    
    </Flex>
  );
}
